/**
 * TokenHeader — Displays token identity: logo, name, symbol, category badge,
 * verification status, and current price.
 *
 * Story 4.2 — Task 2
 */

import type { TokenDetailMetadata, TokenDetailStats } from "@repo/shared-types";

import { resolveApiAssetUrl } from "../../lib/api";
import { cn } from "../../lib/cn";
import { formatPrice, formatPriceChange } from "../../lib/format-number";

type TokenHeaderProps = {
	metadata: TokenDetailMetadata;
	stats: TokenDetailStats | null;
};

const categoryColors: Record<string, string> = {
	AI: "bg-pastel-blue/60 text-blue-700",
	DeFi: "bg-pastel-green/60 text-green-700",
	Gaming: "bg-pastel-purple/60 text-purple-700",
	Meme: "bg-pastel-peach/60 text-orange-700",
	Creator: "bg-pastel-pink/60 text-pink-700",
	Social: "bg-pastel-mint/60 text-teal-700",
	Infrastructure: "bg-pastel-light-gray text-pastel-gray",
	Other: "bg-pastel-light-gray text-pastel-gray",
};

const categorySuits: Record<string, string> = {
	AI: "\u2666",
	DeFi: "\u2660",
	Gaming: "\u2663",
	Creator: "\u2665",
};

function VerificationBadge({ status }: { status: string | null }) {
	if (status === "verified") {
		return (
			<span
				className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700"
				title="This token has been verified by the 0G team"
				aria-label="0G Verified"
			>
				<svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
					<path
						fillRule="evenodd"
						d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
						clipRule="evenodd"
					/>
				</svg>
				0G Verified
			</span>
		);
	}
	if (status === "warned" || status === "flagged") {
		return (
			<span
				className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-700"
				title="This token has been flagged — trade with caution"
				aria-label="Warning"
			>
				<svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
					<path
						fillRule="evenodd"
						d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z"
						clipRule="evenodd"
					/>
				</svg>
				Warning
			</span>
		);
	}
	return null;
}

export function TokenHeader({ metadata, stats }: TokenHeaderProps) {
	const categoryStyle = categoryColors[metadata.category ?? "Other"] ?? categoryColors.Other;
	const suitIcon = categorySuits[metadata.category ?? ""];
	const priceChange = formatPriceChange(stats?.priceChange24h);

	return (
		<div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
			<div className="flex items-center gap-4">
				{metadata.logoUrl ? (
					<img
						src={resolveApiAssetUrl(metadata.logoUrl) ?? undefined}
						alt={`${metadata.name} logo`}
						className="h-14 w-14 rounded-full object-cover flex-shrink-0"
					/>
				) : (
					<div className="h-14 w-14 rounded-full bg-pastel-light-gray flex items-center justify-center flex-shrink-0">
						<span className="text-lg font-bold text-pastel-gray">
							{metadata.symbol.slice(0, 2)}
						</span>
					</div>
				)}

				<div>
					<div className="flex items-center gap-2 flex-wrap">
						<h1 className="text-2xl font-bold text-pastel-black">{metadata.name}</h1>
						<span className="text-lg text-pastel-gray font-medium">{metadata.symbol}</span>
						<VerificationBadge
							status={
								metadata.verifiedAt ? "verified" : metadata.status === "flagged" ? "warned" : null
							}
						/>
					</div>
					<div className="flex items-center gap-2 mt-1">
						{metadata.category && (
							<span
								className={cn(
									"inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium",
									categoryStyle
								)}
							>
								{suitIcon && <span aria-hidden="true">{suitIcon}</span>}
								{metadata.category}
							</span>
						)}
					</div>
				</div>
			</div>

			<div className="text-right sm:text-right">
				<p className="text-2xl font-bold text-pastel-black" data-testid="token-price">
					{formatPrice(stats?.price)}
				</p>
				<p
					className={cn("text-sm font-medium mt-0.5", priceChange.className)}
					data-testid="price-change"
				>
					{stats?.priceChange24h !== undefined && stats.priceChange24h > 0 && (
						<span aria-hidden="true">&uarr; </span>
					)}
					{stats?.priceChange24h !== undefined && stats.priceChange24h < 0 && (
						<span aria-hidden="true">&darr; </span>
					)}
					{priceChange.text}
				</p>
			</div>
		</div>
	);
}

export function TokenHeaderSkeleton() {
	return (
		<div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between animate-pulse">
			<div className="flex items-center gap-4">
				<div className="h-14 w-14 rounded-full bg-pastel-light-gray" />
				<div>
					<div className="h-7 w-40 bg-pastel-light-gray rounded mb-2" />
					<div className="h-5 w-24 bg-pastel-light-gray rounded" />
				</div>
			</div>
			<div className="text-right">
				<div className="h-7 w-28 bg-pastel-light-gray rounded mb-2 ml-auto" />
				<div className="h-5 w-16 bg-pastel-light-gray rounded ml-auto" />
			</div>
		</div>
	);
}
